import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Button,
  Icon,
  TextField,
  Typography,
  SelectMenu,
  Spinner,
  Checkbox,
  Tooltip,
  Modal,
} from '@scale/scaleui';
import type { Variant } from '@scale/scaleui/dist/v1/types';
import { TextInput } from 'design-system';
import { toast } from 'react-toastify';
import {
  WorkerSkillEntryStatus,
  IExtendedSignupFlow,
  SignupFlowType,
  UNIFIED_FLOWS,
  SignupFlowConditionGroupType,
} from '@scale/shared-types';
import {
  useSignupFlowsQuery,
  useSignupFlowNamesQuery,
  useFetchSignupFlowQuery,
  useJobPostsForSignupFlowQuery,
  queryClient,
  createSignupFlowRequest,
  updateSignupFlowRequest,
  fetchCampaignsForSignupFlow,
  fetchJobPostsForSignupFlow,
  SignupFlowResponse,
} from '../../api';
import SignupFlowCountrySelect from './SignupFlowCountrySelect';
import SelectTag from 'react-scale/corp/components/Lookup/sections/SelectTag';
import { useMutation } from 'react-query';
import { HTTPError } from 'react-scale/lib/api-fetch';
import isemail from 'isemail';
import { useOCPStore } from '../../hooks/useOCPStore';
import PanelEntry from '../PanelEntry';
import { composeSignupFlowRequestBody } from '@scale/shared/onboardingControlPanel';
import SelectWorkerSource from 'react-scale/corp/components/UI/SelectWorkerSource';
import { SelectWorkerSkill } from 'react-scale/corp/components/UI/SelectWorkerSkill';
import SelectOnboardingFlow from 'react-scale/corp/components/UI/SelectOnboardingFlow';
import { SignupFlowIntendedWorkerSkills } from './SignupFlowIntendedWorkerSkills';
import { SelectProjectsInSignupFlow } from 'react-scale/corp/components/UI/SelectProjects';
import { TEST_ACCOUNT_TAG_ID } from '@scale/shared/defaults';
import Select, { SelectOption } from 'react-scale/corp/components/UI/Select';
import SignupFlowConditionalConfigInput from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/signupFlow/SignupFlowConditionalConfigInput';

const splitCommaList = (inputList: string) =>
  inputList
    .split(/,+/)
    .map(x => x.trim())
    .filter(x => x.length);
const splitList = (inputList: string) => inputList.split(/[\s,]+/).filter(x => x.length);

enum IntendedProjectSource {
  IntendedProjects = 'intendedProjects',
  IntendedWorkerSkills = 'intendedWorkerSkills',
}

// eslint-disable-next-line complexity
export const signupFlowFieldsMatch = (
  unsaved: Partial<IExtendedSignupFlow>,
  original?: SignupFlowResponse,
): boolean => {
  if (!original) {
    return false;
  }

  for (const key in unsaved) {
    switch (key) {
      case 'tags':
        if (
          !_.isEqual(
            (unsaved.tags || []).map(t => t.value),
            (original.tags || []).map(t => t._id),
          )
        ) {
          return false;
        }
        break;
      case 'intendedProjects':
        if (
          !_.isEqual(
            (unsaved.intendedProjects || []).map(p => p.value),
            (original.intendedProjects || []).map(p => p._id),
          )
        ) {
          return false;
        }
        break;
      case 'emails':
        if (!_.isEqual(unsaved.emails, original.emails)) {
          return false;
        }
        break;
      case 'whitelistGreenhouseJobIds':
      case 'whitelistGreenhouseJobStageNames':
        if (!_.isEqual(unsaved[key], original.signupFlow[key])) {
          return false;
        }
        break;
      case 'allowlistCountryCodes': {
        const unsavedSet = new Set(unsaved[key]);
        const originalSet = new Set(original.signupFlow[key]);
        if (!_.isEqual(unsavedSet, originalSet)) {
          return false;
        }
        break;
      }
      case 'allowlistWorkerTeamsForExistingUsers':
      case 'workerSkillGrant':
      case 'whitelistRegex':
        if (!_.isEqual(unsaved[key], original.signupFlow[key])) {
          return false;
        }
        break;
      default:
        if ((unsaved as any)[key] !== (original.signupFlow as any)[key]) {
          return false;
        }
    }
  }
  return true;
};

const validateWorkerSkillGrant = (signupFlowConfig: Partial<IExtendedSignupFlow>) => {
  for (const grant of signupFlowConfig.workerSkillGrants || []) {
    if ((grant.workerSkillId && !grant.status) || (!grant.workerSkillId && grant.status)) {
      return false;
    }
  }
  return true;
};

const validateConditionalConfig = (signupFlowConfig: Partial<IExtendedSignupFlow>): string => {
  const isConditional = !!signupFlowConfig.conditionalConfig;
  if (isConditional && !signupFlowConfig.fallbackFlowId) {
    return 'Conditional signup flows must have a fallback flow ID';
  }
  if (isConditional && !signupFlowConfig.conditionalConfig?.conditions?.length) {
    return 'Conditional signup flows must have at least one condition';
  }
  if (!isConditional && signupFlowConfig.conditionalScreeningConfigId) {
    return 'Only conditional signup flows can have a custom screening';
  }

  if (!isConditional && signupFlowConfig.autoGrantIntendedWorkerSkills) {
    return 'Only conditional signup flows can auto-grant intended worker skills';
  }

  if (!isConditional && signupFlowConfig.conditionalTags?.length) {
    return 'Only conditional signup flows can have conditional tags';
  }

  if (!isConditional && signupFlowConfig.fallbackFlowId) {
    return 'Only conditional signup flows can have a fallback flow';
  }

  for (const group of signupFlowConfig.conditionalConfig?.conditions ?? []) {
    // Don't need to validate LLM screening conditions
    if (group.type !== SignupFlowConditionGroupType.Sync) {
      continue;
    }
    if (!group.conditions?.length) {
      return 'Conditional signup flows must have at least one qualification condition';
    }
    if (
      group.conditions.some(
        condition =>
          !condition.signalType ||
          !condition.field ||
          !condition.operator ||
          condition.value === undefined,
      )
    ) {
      return 'Fast-lane signup flows must have all required fields for each qualification condition';
    }
  }
  return '';
};

export const validateSignupFlowConfig = (
  signupFlowConfig: Partial<IExtendedSignupFlow>,
  ocpFlowId?: string,
): string => {
  if (!signupFlowConfig.name) {
    return 'Signup flow must have a name';
  } else if (!validateWorkerSkillGrant(signupFlowConfig)) {
    return 'Worker skill grant must have a worker skill and status selected';
  } else if (signupFlowConfig.whitelistRegex?.flags && !signupFlowConfig.whitelistRegex?.regex) {
    return 'Regex flags are set but regex expression is not';
  } else if (!signupFlowConfig.allowlistCountryCodes?.length) {
    return 'Signup flow must have at least one allowed country';
  } else if (ocpFlowId && !/^[a-fA-F0-9]{24}$/.test(ocpFlowId)) {
    return 'Please save draft or publish the flow before creating a signup flow';
  } else if (signupFlowConfig.tags?.some(tag => tag.value === TEST_ACCOUNT_TAG_ID)) {
    return 'Signup flows cannot grant the test account tag';
  } else if (validateConditionalConfig(signupFlowConfig)) {
    return validateConditionalConfig(signupFlowConfig);
  }
  return '';
};

const getIntendedProjectSource = (
  signupFlowConfig: Partial<IExtendedSignupFlow>,
): IntendedProjectSource => {
  const hasProjects = !_.isEmpty(signupFlowConfig.intendedProjects);
  const hasWorkerSkills = !_.isEmpty(signupFlowConfig.intendedWorkerSkills);
  if (signupFlowConfig.autoGrantIntendedWorkerSkills) {
    return IntendedProjectSource.IntendedProjects;
  }

  if (signupFlowConfig.type === SignupFlowType.FastLane || (hasWorkerSkills && !hasProjects)) {
    return IntendedProjectSource.IntendedWorkerSkills;
  }

  return IntendedProjectSource.IntendedProjects;
};

export const isSignupFlowConfigEmpty = (value: any): boolean => {
  if (_.isNil(value) || !value) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return _.every(value, isSignupFlowConfigEmpty);
  }
  return false;
};

interface MutateSignupFlowProps {
  signupFlowConfig: Partial<IExtendedSignupFlow>;
  ocpFlowId: string;
  create?: boolean;
}

// eslint-disable-next-line complexity
const updateSignupFlow = async ({
  signupFlowConfig,
  ocpFlowId,
  create = false,
}: MutateSignupFlowProps) => {
  let response;
  if (
    (!signupFlowConfig._id && !create) ||
    !signupFlowConfig.name ||
    !signupFlowConfig.allowlistCountryCodes?.length ||
    !ocpFlowId
  ) {
    throw new Error('Invalid signup flow configuration');
  }

  if (
    signupFlowConfig.emails &&
    signupFlowConfig.emails.length &&
    !signupFlowConfig.emails.every(email => isemail.validate(email, { minDomainAtoms: 2 }))
  ) {
    throw new Error('Invalid email(s) found');
  }

  const body = composeSignupFlowRequestBody(
    signupFlowConfig,
    signupFlowConfig.onboardingFlow || ocpFlowId,
  );
  if (create) {
    response = await createSignupFlowRequest(body);
  } else {
    response = await updateSignupFlowRequest(signupFlowConfig._id!, body);
  }
  return response.signupFlow;
};

// eslint-disable-next-line complexity
const SignupFlowConfigInput: React.FC<{}> = () => {
  const {
    currentFlow,
    signupFlowConfig,
    setSignupFlowConfig,
    manuallySet,
    setManuallySet,
    signupFlowId,
    setSignupFlowId,
    finishedFetchingSignupFlowDraft,
    isValidSignupFlowName,
    setIsValidSignupFlowName,
    changedSignupFlowOnboardingFlow,
    setChangedSignupFlowOnboardingFlow,
  } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    signupFlowConfig: state.unsavedSignupFlow,
    setSignupFlowConfig: state.setUnsavedSignupFlow,
    manuallySet: state.manuallySetSignupFlow,
    setManuallySet: state.setManuallySetSignupFlow,
    signupFlowId: state.selectedSignupFlowId,
    setSignupFlowId: state.setSelectedSignupFlowId,
    finishedFetchingSignupFlowDraft: state.finishedFetchingSignupFlowDraft,
    isValidSignupFlowName: state.isValidSignupFlowName,
    setIsValidSignupFlowName: state.setIsValidSignupFlowName,
    changedSignupFlowOnboardingFlow: state.changedSignupFlowOnboardingFlow,
    setChangedSignupFlowOnboardingFlow: state.setChangedSignupFlowOnboardingFlow,
  }));

  const [isFirstEffectComplete, setIsFirstEffectComplete] = useState<boolean>(false);

  const [intendedProjectSource, setIntendedProjectSource] = useState<IntendedProjectSource>(() =>
    getIntendedProjectSource(signupFlowConfig),
  );

  const showIntendedProjects =
    signupFlowConfig.type !== SignupFlowType.FastLane &&
    intendedProjectSource === IntendedProjectSource.IntendedProjects;

  const signupFlowsQuery = useSignupFlowsQuery(currentFlow?._id);
  const signupFlowNamesQuery = useSignupFlowNamesQuery();
  const signupFlowQuery = useFetchSignupFlowQuery(signupFlowId);
  const jobPostsQuery = useJobPostsForSignupFlowQuery(signupFlowId);

  const [isEmailWhitelistModalOpen, setIsEmailWhitelistModalOpen] = useState(false);

  const [displayWhitelistGreenhouseJobIds, setDisplayWhitelistGreenhouseJobIds] =
    useState<string>('');
  const [displayWhitelistGreenhouseJobStageNames, setDisplayWhitelistGreenhouseJobStageNames] =
    useState<string>('');
  const [displayEmails, setDisplayEmails] = useState<string>('');

  const signupFlowConfigIsEmpty = _.every(signupFlowConfig, isSignupFlowConfigEmpty);
  const signupFlowChanged =
    (!signupFlowId && !signupFlowConfigIsEmpty) ||
    (!!signupFlowId && !signupFlowFieldsMatch(signupFlowConfig, signupFlowQuery.data));

  // If signupFlowConfig is not empty, force user to select that signup flow
  useEffect(() => {
    setIsFirstEffectComplete(false);
    if (!finishedFetchingSignupFlowDraft) return;
    let changed = false;
    if (!signupFlowConfigIsEmpty && signupFlowConfig._id) {
      // Should be in cache because user had to edit a signup flow for unsavedSignupFlow to be set
      const oldSignupFlow = queryClient.getQueryData<SignupFlowResponse>([
        'signupFlow',
        signupFlowConfig._id,
      ]);
      if (!signupFlowFieldsMatch(signupFlowConfig, oldSignupFlow)) {
        // If signup flow name exists and is not found, it is a draft
        const matchedFlow = signupFlowsQuery.data?.find(
          flow => flow.name === signupFlowConfig.name,
        );
        if (matchedFlow) {
          setSignupFlowConfig({ ...signupFlowConfig, _id: matchedFlow.id });
          setSignupFlowId(matchedFlow.id);
        } else {
          setSignupFlowConfig(signupFlowConfig);
          setSignupFlowId('');
        }
        setManuallySet(true);
        changed = true;
      }
    } else if (!signupFlowConfigIsEmpty) {
      setSignupFlowId('');
      setSignupFlowConfig(signupFlowConfig);
      setManuallySet(true);
      changed = true;
    }
    if (changed) {
      setDisplayWhitelistGreenhouseJobIds(
        signupFlowConfig.whitelistGreenhouseJobIds?.join(', ') ?? '',
      );
      setDisplayWhitelistGreenhouseJobStageNames(
        signupFlowConfig.whitelistGreenhouseJobStageNames?.join(', ') ?? '',
      );
      setDisplayEmails(signupFlowConfig.emails?.join('\n') ?? '');
    } else {
      setSignupFlowConfig({});
      setDisplayWhitelistGreenhouseJobIds('');
      setDisplayWhitelistGreenhouseJobStageNames('');
      setDisplayEmails('');
    }
    setIsFirstEffectComplete(true);
  }, [currentFlow?._id, signupFlowsQuery.isLoading, finishedFetchingSignupFlowDraft]);

  useEffect(() => {
    if (!isFirstEffectComplete) return;
    if (signupFlowId && signupFlowQuery.data?.signupFlow && !manuallySet) {
      setSignupFlowConfig({
        ...signupFlowQuery.data.signupFlow,
        whitelistGreenhouseJobIds: signupFlowQuery.data.signupFlow.whitelistGreenhouseJobIds,
        whitelistGreenhouseJobStageNames:
          signupFlowQuery.data.signupFlow.whitelistGreenhouseJobStageNames,
        tags: signupFlowQuery.data?.tags?.map(t => ({ value: t._id, label: t.name })) ?? [],
        intendedProjects:
          signupFlowQuery.data?.intendedProjects?.map(p => ({
            value: p._id,
            label: p.name,
          })) ?? [],
        emails: signupFlowQuery.data?.emails,
      });
      setDisplayWhitelistGreenhouseJobIds(
        signupFlowQuery.data.signupFlow.whitelistGreenhouseJobIds?.join(', ') ?? '',
      );
      setDisplayWhitelistGreenhouseJobStageNames(
        signupFlowQuery.data.signupFlow.whitelistGreenhouseJobStageNames?.join(', ') ?? '',
      );
      setDisplayEmails(signupFlowQuery.data?.emails?.join('\n') ?? '');
    } else if (!signupFlowId && !manuallySet) {
      setSignupFlowConfig({});
      setDisplayWhitelistGreenhouseJobIds('');
      setDisplayWhitelistGreenhouseJobStageNames('');
      setDisplayEmails('');
    }
  }, [signupFlowId, signupFlowQuery.data, isFirstEffectComplete, manuallySet]);

  useEffect(() => {
    if (isFirstEffectComplete && !signupFlowChanged) {
      setManuallySet(false);
    }
  }, [isFirstEffectComplete, signupFlowChanged]);

  useEffect(() => {
    setIntendedProjectSource(getIntendedProjectSource(signupFlowConfig));
  }, [
    signupFlowConfig._id,
    signupFlowConfig?.conditionalConfig?.conditions?.length,
    signupFlowConfig.autoGrantIntendedWorkerSkills,
  ]);

  const createSignupFlowMutation = useMutation(updateSignupFlow, {
    onSuccess: savedSignupFlow => {
      toast(`${savedSignupFlow.name} created successfully!`, {
        type: 'success',
        autoClose: 5000,
        position: 'top-right',
      });
      queryClient.invalidateQueries([currentFlow?._id, 'signupFlows']);
      queryClient.invalidateQueries([`signup-flow-info-${currentFlow?._id}`]);
      queryClient.invalidateQueries(['fastLaneChain']);
      setManuallySet(false);
      setSignupFlowId(savedSignupFlow._id);
      setSignupFlowConfig({});
    },
    onError: error => {
      if (error instanceof HTTPError) {
        toast(`Failed to create ${signupFlowConfig.name}, ${error.response?.message}`, {
          type: 'error',
          autoClose: 5000,
        });
      } else {
        toast(`Failed to create ${signupFlowConfig.name}, ${error}`, {
          type: 'error',
          autoClose: 5000,
        });
      }
    },
  });

  const updateSignupFlowMutation = useMutation(updateSignupFlow, {
    onSuccess: savedSignupFlow => {
      toast(`${savedSignupFlow.name} updated successfully!`, {
        type: 'success',
        autoClose: 5000,
        position: 'top-right',
      });
      queryClient.invalidateQueries(['signupFlow', savedSignupFlow._id]);
      queryClient.invalidateQueries(['fastLaneChain']);
      setManuallySet(false);
      setSignupFlowId(savedSignupFlow._id);
      setSignupFlowConfig({});
      if (changedSignupFlowOnboardingFlow) {
        queryClient.invalidateQueries([currentFlow?._id, 'signupFlows']);
        queryClient.invalidateQueries([`signup-flow-info-${currentFlow?._id}`]);
        setSignupFlowId('');
        setChangedSignupFlowOnboardingFlow(false);
      }
    },
    onError: error => {
      if (error instanceof HTTPError) {
        toast(`Failed to update ${signupFlowConfig.name}, ${error.response?.message}`, {
          type: 'error',
          autoClose: 5000,
        });
      } else {
        toast(`Failed to update ${signupFlowConfig.name}, ${error}`, {
          type: 'error',
          autoClose: 5000,
        });
      }
    },
  });

  const conditionalConfigEnabled = currentFlow?._id && UNIFIED_FLOWS.includes(currentFlow._id);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${label} copied to clipboard!`, {
        type: 'success',
        autoClose: 2000,
        position: 'top-right',
      });
    } catch (err) {
      toast(`Failed to copy ${label}`, {
        type: 'error',
        autoClose: 3000,
      });
    }
  };

  return (
    <PanelEntry title="Signup Flows">
      {isFirstEffectComplete ? (
        <>
          <SelectMenu
            className="my-1"
            options={[
              { id: '', name: '+ Add New Signup Flow' },
              ...(signupFlowsQuery.data?.map(flow => ({
                id: flow.id,
                name: flow.name,
                ...(flow.active
                  ? {}
                  : { tag: { variant: 'neutral' as Variant, value: 'Inactive', size: 'sm' } }),
              })) || []),
            ]}
            selectedOptionId={signupFlowId}
            setSelected={setSignupFlowId}
            disabled={
              (signupFlowChanged && (!signupFlowId || !!signupFlowQuery.data)) ||
              signupFlowsQuery.data?.length === 0
            }
            helperText={
              signupFlowChanged && (!signupFlowId || !!signupFlowQuery.data)
                ? 'Please save changes before selecting a new signup flow'
                : ''
            }
          />
          {signupFlowId && signupFlowQuery.isLoading && (
            <div className="flex justify-center">
              <Spinner className="mt-2" size="sm" variant="primary" />
            </div>
          )}
          <div className="grid grid-cols-4 items-center justify-items-start gap-y-8 gap-x-3 mt-4">
            {signupFlowId && signupFlowQuery.data ? (
              <>
                <Typography.Paragraph3 className="col-span-1">
                  Signup Flow Token
                </Typography.Paragraph3>
                <div className="col-span-3 flex items-center gap-2">
                  <Typography.Paragraph3>
                    {signupFlowQuery.data.signupFlow?.token || "Error: couldn't find token"}
                  </Typography.Paragraph3>
                  {signupFlowQuery.data.signupFlow?.token && (
                    <Icon
                      icon={['far', 'copy']}
                      size="sm"
                      className="cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() =>
                        copyToClipboard(
                          signupFlowQuery.data.signupFlow?.token || '',
                          'Signup Flow Token',
                        )
                      }
                    />
                  )}
                </div>
                <Typography.Paragraph3 className="col-span-1">Signup Flow ID</Typography.Paragraph3>
                <div className="col-span-3 flex items-center gap-2">
                  <Typography.Paragraph3>{signupFlowId}</Typography.Paragraph3>
                  <Icon
                    icon={['far', 'copy']}
                    size="sm"
                    className="cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() => copyToClipboard(signupFlowId, 'Signup Flow ID')}
                  />
                </div>
                <Typography.Paragraph3 className="col-span-1">Is Active</Typography.Paragraph3>
                <Checkbox
                  checked={signupFlowConfig.active}
                  className="col-span-3"
                  id="checkbox"
                  onChange={async e => {
                    const isChecked = e.target.checked;
                    if (!isChecked) {
                      const response = await fetchCampaignsForSignupFlow(signupFlowId);
                      if (response.length) {
                        toast(
                          `Cannot deactivate as it is present in campaign ${response
                            .map((campaign: { name: string }) => campaign.name)
                            .join(', ')}`,
                          {
                            type: 'error',
                            autoClose: 5000,
                          },
                        );
                        return;
                      }
                      const jobPosts = await fetchJobPostsForSignupFlow(signupFlowId);
                      if (jobPosts.filter(jobPost => jobPost.isActive).length) {
                        toast(
                          `Cannot deactivate as it is present in active job post ${jobPosts
                            .map(jobPost => jobPost.name)
                            .join(', ')}`,
                          {
                            type: 'error',
                            autoClose: 5000,
                          },
                        );
                        return;
                      }
                    }
                    setSignupFlowConfig({ ...signupFlowConfig, active: isChecked });
                  }}
                  variant="primary"
                />
              </>
            ) : (
              <>
                <Typography.Paragraph3 className="col-span-1">
                  Signup Flow Name *
                </Typography.Paragraph3>
                <TextField
                  className="w-5/6 col-span-3"
                  value={signupFlowConfig.name ?? ''}
                  onChange={e => {
                    setSignupFlowConfig({ ...signupFlowConfig, name: e.target.value });
                    setIsValidSignupFlowName(
                      !!signupFlowNamesQuery.data &&
                        !signupFlowNamesQuery.data.includes(e.target.value),
                    );
                  }}
                  helperText={
                    !signupFlowConfig.name
                      ? ''
                      : signupFlowNamesQuery.isLoading
                      ? 'Validating...'
                      : signupFlowNamesQuery.data?.includes(signupFlowConfig.name)
                      ? 'Name already exists'
                      : ''
                  }
                  error={
                    !!signupFlowConfig.name &&
                    signupFlowNamesQuery.data?.includes(signupFlowConfig.name)
                  }
                />
              </>
            )}

            {conditionalConfigEnabled && (
              <div className="col-span-4 w-full mt-4">
                <SignupFlowConditionalConfigInput
                  signupFlowConfig={signupFlowConfig}
                  setSignupFlowConfig={setSignupFlowConfig}
                  signupFlowsForOnboardingFlow={signupFlowsQuery.data ?? []}
                  setSignupFlowId={setSignupFlowId}
                  disableFlowNavigation={
                    signupFlowChanged && (!signupFlowId || !!signupFlowQuery.data)
                  }
                />
              </div>
            )}

            <div className="col-span-1 flex items-center self-start pr-1">
              <Typography.Paragraph3>Worker Source Granted</Typography.Paragraph3>
              <Tooltip
                text={'The origin or channel through which a user was acquired'}
                target={
                  <div>
                    <Icon icon={['fas', 'circle-info']} />
                  </div>
                }
                side="bottom"
              />
            </div>
            <SelectWorkerSource
              className="w-5/6 col-span-3"
              multi={false}
              includeNoWorkerSource={true}
              value={signupFlowConfig.workerSource}
              onChange={(selectedOption: { label: string; value: string } | undefined) => {
                setSignupFlowConfig({ ...signupFlowConfig, workerSource: selectedOption?.value });
              }}
            />
            <div className="col-span-1 flex items-center self-start pr-1">
              <Typography.Paragraph3>Tags Granted</Typography.Paragraph3>
              <Tooltip
                text={'Labels to help categorize or identify specific attributes of the user'}
                target={
                  <div>
                    <Icon icon={['fas', 'circle-info']} />
                  </div>
                }
                side="bottom"
              />
            </div>
            <SelectTag
              className="w-5/6 col-span-3"
              showTagId={false}
              value={signupFlowConfig.tags || []}
              onChange={tags => {
                setSignupFlowConfig({ ...signupFlowConfig, tags });
              }}
              multi={true}
            />
            {signupFlowId && !_.isEmpty(signupFlowConfig.workerSkillGrants) && (
              <>
                <div className="col-span-1 flex items-center self-start pr-1">
                  <Typography.Paragraph3>Worker Skills Granted</Typography.Paragraph3>
                  <Tooltip
                    text={
                      'A specific skill assigned to users that can qualify them for certain projects'
                    }
                    target={
                      <div>
                        <Icon icon={['fas', 'circle-info']} />
                      </div>
                    }
                    side="bottom"
                  />
                </div>
                <div className="flex flex-col col-span-3 gap-2">
                  {signupFlowConfig.workerSkillGrants?.map((grant, index) => (
                    <div key={index} className="flex items-center gap-2 w-full">
                      <SelectWorkerSkill
                        className="w-52"
                        value={grant.workerSkillId}
                        onChange={(value: string | undefined) => {
                          setSignupFlowConfig({
                            ...signupFlowConfig,
                            workerSkillGrants: (signupFlowConfig.workerSkillGrants ?? [])?.map(
                              (grant, i) =>
                                i === index && value
                                  ? {
                                      ...grant,
                                      workerSkillId: value,
                                    }
                                  : grant,
                            ),
                          });
                        }}
                      />
                      {grant.workerSkillId && (
                        <SelectMenu
                          className="w-36"
                          selectedOptionId={grant.status ?? ''}
                          placeholder="Status"
                          options={Object.values(WorkerSkillEntryStatus).map(status => ({
                            id: status,
                            name: status,
                          }))}
                          setSelected={status => {
                            setSignupFlowConfig({
                              ...signupFlowConfig,
                              workerSkillGrants: signupFlowConfig.workerSkillGrants?.map(
                                (grant, i) =>
                                  i === index
                                    ? {
                                        ...grant,
                                        status: status as WorkerSkillEntryStatus,
                                      }
                                    : grant,
                              ),
                            });
                          }}
                        />
                      )}
                      <Icon
                        className="cursor-pointer"
                        icon={['fas', 'times']}
                        size="sm"
                        variant="neutral"
                        onClick={() => {
                          setSignupFlowConfig({
                            ...signupFlowConfig,
                            workerSkillGrants: signupFlowConfig.workerSkillGrants?.filter(
                              (_, i) => i !== index,
                            ),
                          });
                        }}
                      />
                    </div>
                  ))}

                  <div className={!_.isEmpty(signupFlowConfig.workerSkillGrants) ? 'mt-2' : ''}>
                    <Button
                      variant="neutral"
                      size="sm"
                      onClick={() => {
                        setSignupFlowConfig({
                          ...signupFlowConfig,
                          workerSkillGrants: [
                            ...(signupFlowConfig.workerSkillGrants ?? []),
                            {
                              workerSkillId: '',
                              status: WorkerSkillEntryStatus.SelfReported,
                            },
                          ],
                        });
                      }}
                      disabled={signupFlowConfig.workerSkillGrants?.some(
                        grant => !grant.workerSkillId,
                      )}
                    >
                      Add Worker Skill
                    </Button>
                  </div>
                </div>
              </>
            )}

            {signupFlowConfig.type !== SignupFlowType.FastLane && (
              <>
                <div className="col-span-1 flex items-center self-start pr-1">
                  <Typography.Paragraph3>Intended Skills Source</Typography.Paragraph3>
                  <Tooltip
                    text={
                      'Determine if users are assigned screenings based on projects or worker skills'
                    }
                    target={
                      <div>
                        <Icon icon={['fas', 'circle-info']} />
                      </div>
                    }
                    side="bottom"
                  />
                </div>
                <Select
                  className="w-5/6 col-span-3"
                  value={
                    intendedProjectSource === IntendedProjectSource.IntendedProjects
                      ? IntendedProjectSource.IntendedProjects
                      : IntendedProjectSource.IntendedWorkerSkills
                  }
                  onChange={(option: { label: string; value: IntendedProjectSource }) => {
                    setIntendedProjectSource(
                      option.value === IntendedProjectSource.IntendedProjects
                        ? IntendedProjectSource.IntendedProjects
                        : IntendedProjectSource.IntendedWorkerSkills,
                    );
                    setSignupFlowConfig({
                      ...signupFlowConfig,
                      intendedProjects:
                        option.value === IntendedProjectSource.IntendedProjects
                          ? signupFlowConfig.intendedProjects
                          : [],
                      intendedWorkerSkills:
                        option.value === IntendedProjectSource.IntendedWorkerSkills
                          ? signupFlowConfig.intendedWorkerSkills
                          : [],
                    });
                  }}
                  disabled={signupFlowConfig.autoGrantIntendedWorkerSkills}
                  options={[
                    {
                      label: 'Intended Projects',
                      value: IntendedProjectSource.IntendedProjects,
                    },
                    {
                      label: 'Intended Worker Skills',
                      value: IntendedProjectSource.IntendedWorkerSkills,
                    },
                  ]}
                />
              </>
            )}

            {showIntendedProjects && (
              <>
                <div className="col-span-1 flex items-center self-start pr-1">
                  <Typography.Paragraph3>Intended Projects</Typography.Paragraph3>
                  <Tooltip
                    text={
                      'OCP will attempt to direct assign one of these projects at the end of onboarding if they pass the qualifications'
                    }
                    target={
                      <div>
                        <Icon icon={['fas', 'circle-info']} />
                      </div>
                    }
                    side="bottom"
                  />
                </div>
                <SelectProjectsInSignupFlow
                  className="w-5/6 col-span-3"
                  value={signupFlowConfig.intendedProjects || []}
                  onChange={(intendedProjects: SelectOption[] | undefined) => {
                    setSignupFlowConfig({ ...signupFlowConfig, intendedProjects });
                  }}
                  multi={true}
                />
              </>
            )}

            {!showIntendedProjects &&
              signupFlowConfig.type !== SignupFlowType.FastLane &&
              !signupFlowConfig.autoGrantIntendedWorkerSkills && (
                <SignupFlowIntendedWorkerSkills
                  signupFlowConfig={signupFlowConfig}
                  setSignupFlowConfig={setSignupFlowConfig}
                />
              )}

            {signupFlowId && signupFlowQuery.data && (
              <>
                <Typography.Paragraph3 className="col-span-1">
                  Onboarding Flow *
                </Typography.Paragraph3>
                <SelectOnboardingFlow
                  className="w-5/6 col-span-3"
                  value={signupFlowConfig.onboardingFlow}
                  onChange={(value: string | undefined) => {
                    if (!value) {
                      if (currentFlow?._id) {
                        setSignupFlowConfig({
                          ...signupFlowConfig,
                          onboardingFlow: currentFlow._id,
                        });
                        setChangedSignupFlowOnboardingFlow(false);
                      } else {
                        return;
                      }
                    } else {
                      setSignupFlowConfig({ ...signupFlowConfig, onboardingFlow: value });
                      setChangedSignupFlowOnboardingFlow(true);
                    }
                  }}
                />
              </>
            )}

            <div className="flex flex-col col-span-4">
              <Typography.Subtitle3>Whitelists</Typography.Subtitle3>
              <Typography.Caption>
                Users meeting <b>any</b> of the following whitelists can sign up with this flow. If
                there are no whitelists, all users are whitelisted.
              </Typography.Caption>
            </div>
            <Typography.Paragraph3 className="col-span-1">Email Whitelist</Typography.Paragraph3>
            <div className="col-span-3 flex items-center gap-6">
              <Checkbox
                checked={signupFlowConfig.requiresWhitelistedEmail ?? false}
                onChange={e => {
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    requiresWhitelistedEmail: e.target.checked,
                  });
                }}
                variant="primary"
              />
              <div className="flex items-center">
                <Button
                  onClick={() => setIsEmailWhitelistModalOpen(true)}
                  variant="secondary"
                  size="sm"
                  disabled={!signupFlowConfig.requiresWhitelistedEmail}
                >
                  Edit Email Whitelist
                </Button>
                <Modal
                  open={isEmailWhitelistModalOpen}
                  onClose={() => setIsEmailWhitelistModalOpen(false)}
                  title="Edit Email Whitelist"
                  innerClassName="w-2/3 h-2/3"
                >
                  <Typography.Paragraph3 className="mb-2">
                    Changes are automatically saved.
                  </Typography.Paragraph3>
                  <TextInput
                    placeholder="Enter emails separated by commas or whitespace"
                    value={displayEmails}
                    onChange={e => {
                      setSignupFlowConfig({
                        ...signupFlowConfig,
                        emails: splitList(e.target.value),
                      });
                      setDisplayEmails(e.target.value);
                    }}
                    fullWidth
                    multiline
                  />
                </Modal>
              </div>
            </div>
            <Typography.Paragraph3 className="col-span-1 self-start pt-1">
              Regex Whitelist
            </Typography.Paragraph3>
            <div className="col-span-3 flex flex-col gap-y-3">
              <TextField
                className="w-5/6"
                helperText={
                  <>
                    Regex Expression. Be sure to include escape characters as needed.{' '}
                    <a
                      href="https://regex101.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Test with a regex tester.
                    </a>
                  </>
                }
                placeholder="Regex to whitelist user emails"
                value={signupFlowConfig.whitelistRegex?.regex ?? ''}
                onChange={e => {
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    whitelistRegex: {
                      regex: e.target.value,
                      flags: signupFlowConfig.whitelistRegex?.flags ?? '',
                    },
                  });
                }}
              />
              <TextField
                className="w-5/6"
                helperText={
                  <>
                    Regex Flags (Optional). Add flags to make the regex case insensitive, etc.{' '}
                    <a
                      href="https://javascript.info/regexp-introduction#flags"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Read more here.
                    </a>
                  </>
                }
                value={signupFlowConfig.whitelistRegex?.flags ?? ''}
                onChange={e => {
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    whitelistRegex: {
                      flags: e.target.value,
                      regex: signupFlowConfig.whitelistRegex?.regex ?? '',
                    },
                  });
                }}
              />
            </div>
            <Typography.Paragraph3 className="col-span-1 self-start pt-1">
              Greenhouse Whitelist
            </Typography.Paragraph3>
            <div className="col-span-3 flex flex-col gap-y-3">
              <TextField
                className="w-5/6"
                helperText="Greenhouse Job IDs"
                placeholder="Job IDs separated by commas"
                value={displayWhitelistGreenhouseJobIds}
                onChange={e => {
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    whitelistGreenhouseJobIds: splitCommaList(e.target.value),
                  });
                  setDisplayWhitelistGreenhouseJobIds(e.target.value);
                }}
                disabled={!!jobPostsQuery.data?.length}
              />
              <TextField
                className="w-5/6"
                value={displayWhitelistGreenhouseJobStageNames}
                onChange={e => {
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    whitelistGreenhouseJobStageNames: splitCommaList(e.target.value),
                  });
                  setDisplayWhitelistGreenhouseJobStageNames(e.target.value);
                }}
                placeholder="Comma-separated job stage names"
                helperText='Greenhouse Job Stages. Optional: If not provided, will default to "Offer".'
                disabled={!!jobPostsQuery.data?.length}
              />
            </div>
            <div className="flex flex-col col-span-1 items-center self-start pt-1">
              <Typography.Paragraph3>Allowed Countries *</Typography.Paragraph3>
            </div>
            <SignupFlowCountrySelect
              className="col-span-3 self-start"
              isCountryChecked={(countryCode: string) =>
                signupFlowConfig.allowlistCountryCodes?.includes(countryCode) || false
              }
              handleCountryCheck={(countryCodes: string[]) => {
                setSignupFlowConfig({
                  ...signupFlowConfig,
                  allowlistCountryCodes: (() => {
                    const countryCodesSet = new Set(signupFlowConfig.allowlistCountryCodes || []);
                    countryCodes.forEach(countryCode => {
                      countryCodesSet.has(countryCode)
                        ? countryCodesSet.delete(countryCode)
                        : countryCodesSet.add(countryCode);
                    });
                    return Array.from(countryCodesSet);
                  })(),
                });
              }}
            />

            <div className="flex col-span-4 ml-auto mr-2">
              <Tooltip
                text={
                  !isValidSignupFlowName
                    ? 'Signup flow name is not unique'
                    : validateSignupFlowConfig(signupFlowConfig, currentFlow?._id)
                }
                target={
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (signupFlowId) {
                        updateSignupFlowMutation.mutate({
                          signupFlowConfig,
                          ocpFlowId: currentFlow!._id,
                          create: false,
                        });
                      } else {
                        createSignupFlowMutation.mutate({
                          signupFlowConfig,
                          ocpFlowId: currentFlow!._id,
                          create: true,
                        });
                      }
                    }}
                    disabled={
                      !currentFlow ||
                      !isValidSignupFlowName ||
                      !!validateSignupFlowConfig(signupFlowConfig, currentFlow!._id) ||
                      updateSignupFlowMutation.isLoading ||
                      createSignupFlowMutation.isLoading ||
                      !signupFlowChanged
                    }
                  >
                    {signupFlowId
                      ? updateSignupFlowMutation.isLoading
                        ? 'Updating...'
                        : 'Update Signup Flow'
                      : createSignupFlowMutation.isLoading
                      ? 'Creating...'
                      : 'Create Signup Flow'}
                  </Button>
                }
                side="left"
              />
            </div>
          </div>
        </>
      ) : (
        <Spinner className="mt-2" size="sm" variant="primary" />
      )}
    </PanelEntry>
  );
};

export default SignupFlowConfigInput;
